import {
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import type { DocAiLength, DocAiTone } from "@xop/dashboard-shared";

import { generateAiDoc, listCategories, listReleases } from "./api.js";
import { Markdown } from "./markdown.js";
import { BackButton } from "@/components/ui/back-button";

const LENGTHS: { value: DocAiLength; label: string }[] = [
  { value: "short", label: "Short (~300-400 words)" },
  { value: "medium", label: "Medium (~700-1000 words)" },
  { value: "long", label: "Long (~1500-2500 words)" },
];

const TONES: { value: DocAiTone; label: string }[] = [
  { value: "neutral", label: "Neutral" },
  { value: "friendly", label: "Friendly" },
  { value: "formal", label: "Formal" },
  { value: "technical", label: "Technical" },
];

/** Have Claude draft a document grounded in the existing documentation.
 * The dashboard server forwards the prompt to the support-center's
 * `Documents::Generate` service, which talks to the AI gateway with the
 * "content" persona — same path the customer-support admin uses.
 *
 * Two-pane layout (2026-06-06): the prompt form on the LEFT, a LIVE
 * PREVIEW of the generated draft on the RIGHT. The draft used to bounce
 * straight to the edit page on success — where, if its body hadn't
 * populated, the form showed "No file attached" and the doc looked
 * unusable. Now the rendered markdown lands in the preview immediately;
 * the operator reads it, then chooses Open in editor / Generate again. */
export function DocumentAiPage(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [prompt, setPrompt] = useState("");
  const [titleHint, setTitleHint] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [releaseId, setReleaseId] = useState("");
  const [length, setLength] = useState<DocAiLength>("medium");
  const [tone, setTone] = useState<DocAiTone>("neutral");
  const [error, setError] = useState<string | null>(null);

  const { data: cats } = useQuery({
    queryKey: ["docs", "categories"],
    queryFn: listCategories,
  });
  const { data: rels } = useQuery({
    queryKey: ["docs", "releases"],
    queryFn: listReleases,
  });

  const gen = useMutation({
    mutationFn: () =>
      generateAiDoc({
        prompt,
        length,
        tone,
        ...(titleHint ? { title: titleHint } : {}),
        ...(categoryId ? { document_category_id: Number(categoryId) } : {}),
        ...(releaseId ? { default_release_id: Number(releaseId) } : {}),
      }),
    onSuccess: () => {
      // Refresh the tree/lists, but DON'T navigate — keep the draft in the
      // preview pane so the operator can read it right here.
      void qc.invalidateQueries({ queryKey: ["docs"] });
    },
    onError: (e: unknown) =>
      setError(e instanceof Error ? e.message : "Generation failed"),
  });

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    setError(null);
    if (prompt.trim().length < 20) {
      setError("Describe what you want — at least a sentence or two (20+ chars).");
      return;
    }
    gen.mutate();
  };

  const generating = gen.isPending;
  const draft = gen.isSuccess ? gen.data.data : null;

  return (
    <Box sx={{ display: "grid", gap: 2.5 }}>
      <Box component="header">
        <BackButton to="/documentation/manage/new" label="Back" sx={{ mb: 1.5 }} />
        <Typography component="h2" sx={{ mt: 0.5, fontSize: "1.125rem", fontWeight: 600, letterSpacing: "-0.01em" }}>
          Generate with AI
        </Typography>
        <Typography sx={{ mt: 0.5, fontSize: 14, color: "text.disabled" }}>
          Describe what you need. Claude drafts a document grounded in your
          documentation — it appears in the preview to the right; review and
          edit before publishing.
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: 2.5,
          alignItems: "start",
          gridTemplateColumns: { lg: "minmax(0,440px) minmax(0,1fr)" },
        }}
      >
        {/* ── LEFT: prompt form ─────────────────────────────────────────── */}
        <Card sx={{ alignSelf: "start", position: { lg: "sticky" }, top: { lg: 16 } }}>
          <CardContent>
            <Box component="form" onSubmit={submit} sx={{ display: "grid", gap: 2.5 }}>
              <Box sx={{ display: "grid", gap: 0.75 }}>
                <TextField
                  id="ai-prompt"
                  label="What should Claude write?"
                  multiline
                  rows={6}
                  placeholder="e.g. Release notes for v9.2 covering the new realtime dashboard, the SAML-SSO fix, and the deprecated /v1/legacy_export endpoint. Audience: existing customers upgrading from v9.0."
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  inputProps={{ required: true }}
                  fullWidth
                />
                <Typography sx={{ fontSize: "11px", color: "text.disabled" }}>
                  Be specific: who is this for, what should be covered, what
                  shouldn't be. Claude pulls supporting facts from your existing
                  documents and bucket files.
                </Typography>
              </Box>

              <TextField
                id="ai-title"
                label="Title hint (optional)"
                placeholder="If empty, Claude will pick a title from the H1 it writes."
                value={titleHint}
                onChange={(e) => setTitleHint(e.target.value)}
                fullWidth
              />

              <Box sx={{ display: "grid", gap: 2 }}>
                <TextField select label="Category (optional)" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} fullWidth>
                  <MenuItem value="">None</MenuItem>
                  {(cats?.data ?? []).map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>
                      {c.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField select label="Release (optional)" value={releaseId} onChange={(e) => setReleaseId(e.target.value)} fullWidth>
                  <MenuItem value="">None</MenuItem>
                  {(rels?.data ?? []).map((r) => (
                    <MenuItem key={r.id} value={String(r.id)}>
                      v{r.version}
                      {r.name ? ` — ${r.name}` : ""}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "repeat(2, 1fr)" }}>
                <TextField select label="Length" value={length} onChange={(e) => setLength(e.target.value as DocAiLength)} fullWidth>
                  {LENGTHS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField select label="Tone" value={tone} onChange={(e) => setTone(e.target.value as DocAiTone)} fullWidth>
                  {TONES.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              {error && (
                <Box sx={{ borderRadius: 1, bgcolor: (t) => alpha(t.palette.error.main, 0.1), px: 1.5, py: 1, fontSize: 12, color: "error.main" }}>
                  {error}
                </Box>
              )}

              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, borderTop: 1, borderColor: "divider", pt: 2 }}>
                <Button component={RouterLink} to="/documentation/manage/new" variant="text" color="inherit" size="small">
                  Back
                </Button>
                <Button type="submit" variant="contained" size="small" disabled={generating}>
                  {generating ? "Drafting…" : draft ? "Regenerate" : "Generate draft"}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* ── RIGHT: live preview ───────────────────────────────────────── */}
        <Card sx={{ minWidth: 0 }}>
          <CardContent sx={{ display: "grid", gap: 2, minHeight: 280 }}>
            {generating ? (
              <GeneratingState length={length} />
            ) : draft ? (
              <DraftPreview
                title={draft.title}
                content={draft.content ?? ""}
                onOpenEditor={() => navigate(`/documentation/manage/${draft.slug}/edit`)}
                onGenerateAgain={() => gen.reset()}
              />
            ) : (
              <IdlePlaceholder />
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

/** Empty preview pane before the first generation. */
function IdlePlaceholder(): JSX.Element {
  return (
    <Box
      sx={{
        display: "grid",
        placeItems: "center",
        gap: 1,
        textAlign: "center",
        color: "text.disabled",
        py: 6,
      }}
    >
      <Box component="span" sx={{ fontSize: 28, opacity: 0.5 }}>✦</Box>
      <Typography sx={{ fontSize: 14, fontWeight: 500, color: "text.secondary" }}>
        Your draft will appear here
      </Typography>
      <Typography sx={{ fontSize: 12, maxWidth: 320 }}>
        Fill in the brief on the left and hit{" "}
        <Box component="span" sx={{ fontWeight: 500, color: "text.primary" }}>Generate draft</Box>.
        Claude reads your documentation and writes a full document you can
        review here before opening it in the editor.
      </Typography>
    </Box>
  );
}

/** The "Claude is writing…" state, shown in the preview pane. */
function GeneratingState({ length }: { length: DocAiLength }): JSX.Element {
  const eta =
    length === "short" ? "10-20 seconds" : length === "long" ? "40-90 seconds" : "20-60 seconds";
  return (
    <Box sx={{ display: "grid", placeItems: "center", gap: 1.25, textAlign: "center", py: 6 }}>
      <Box
        component="span"
        sx={{
          fontSize: 28,
          color: "warning.main",
          animation: "pulse 2s ease-in-out infinite",
          "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.35 } },
        }}
      >
        ✦
      </Box>
      <Typography sx={{ fontSize: 14, fontWeight: 500, color: "warning.main" }}>
        Generating…
      </Typography>
      <Typography sx={{ fontSize: 12, maxWidth: 340, color: "text.disabled" }}>
        Claude is reading your documentation and writing the draft. This
        usually takes {eta} depending on the requested length.
      </Typography>
    </Box>
  );
}

/** Rendered preview of the generated draft + the next-step actions. */
function DraftPreview({
  title,
  content,
  onOpenEditor,
  onGenerateAgain,
}: {
  title: string;
  content: string;
  onOpenEditor: () => void;
  onGenerateAgain: () => void;
}): JSX.Element {
  const hasBody = content.trim().length > 0;
  return (
    <>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              mb: 0.5,
              fontSize: "10px",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: "success.main",
            }}
          >
            ✦ Draft ready
          </Box>
          <Typography component="h3" sx={{ fontSize: "1.25rem", fontWeight: 600, letterSpacing: "-0.01em", lineHeight: 1.25 }}>
            {title}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", flexShrink: 0, gap: 1 }}>
          <Button type="button" variant="outlined" color="inherit" size="small" onClick={onGenerateAgain} sx={{ bgcolor: "action.hover" }}>
            Generate again
          </Button>
          <Button type="button" variant="contained" size="small" onClick={onOpenEditor}>
            Open in editor
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          borderTop: 1,
          borderColor: "divider",
          pt: 2,
          maxHeight: "62vh",
          overflowY: "auto",
        }}
      >
        {hasBody ? (
          <Box sx={{ maxWidth: "76ch" }}>
            <Markdown source={content} />
          </Box>
        ) : (
          <Typography sx={{ fontSize: 14, color: "text.disabled" }}>
            The draft was created but came back without preview text. Open it in
            the editor to review and add the body.
          </Typography>
        )}
      </Box>

      <Typography sx={{ fontSize: "11px", color: "text.disabled" }}>
        Saved as a <strong>draft</strong> — it isn't published yet. Open it in
        the editor to refine, set a category/release, and publish.
      </Typography>
    </>
  );
}
